<template>
  <nav 
    class="fixed top-0 left-0 right-0 z-[100] pt-6 pb-2 px-4 sm:px-6 lg:px-8 pointer-events-none transition-all duration-300 ease-in-out" 
    role="navigation" 
    aria-label="Hauptnavigation"
    :style="{ 
      transform: navTransform, 
      opacity: navOpacity 
    }"
  >
    <div class="max-w-7xl mx-auto">
      <!-- Navbar -->
      <div 
        :data-project-page="isProjectPage"
        class="rounded-2xl shadow-lg shadow-gray-300/40 dark:shadow-black/50 dark:shadow-2xl px-6 py-4 flex items-center backdrop-blur-md dark:backdrop-blur-xl transition-colors duration-300 pointer-events-auto bg-white/95 dark:bg-gray-900/95 dark:border-gray-800/80 border border-gray-200/50 dark:border-gray-700/60 relative overflow-hidden"
      >
        <!-- Subtle Dark Mode Glow -->
        <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:opacity-100 opacity-0 pointer-events-none"></div>
        <router-link 
          to="/" 
          class="flex-shrink-0 flex items-center hover:opacity-90 transition-opacity focus:outline-none rounded-md p-1 -ml-1 z-20 relative"
          aria-label="Zur Startseite"
          @mouseenter="isNameHovered = true"
          @mouseleave="isNameHovered = false"
        >
          <span 
            :class="[
              'name-text text-lg sm:text-xl font-bold inline-block whitespace-nowrap transition-colors',
              isNameHovered 
                ? `${primaryTextClass} ${primaryTextDarkClass}` 
                : 'text-gray-900 dark:text-gray-100',
              { 'name-hovered': isNameHovered }
            ]"
            :style="navigationAccentColor && isNameHovered ? { color: navigationAccentColor } : {}"
          >
            RE<span 
              :class="`it-text it-underline ${primaryTextClass} ${primaryTextDarkClass}`"
              :style="navigationAccentColor && isNameHovered ? { color: navigationAccentColor } : {}"
            >IT</span>SBERGER
          </span>
        </router-link>
        
        <!-- Flexibler Spacer für konsistenten Abstand - nur auf Desktop -->
        <div class="hidden md:block flex-1 min-w-[2rem] max-w-[20rem] lg:max-w-[24rem] xl:max-w-[28rem]"></div>
        
        <!-- Desktop Navigation -->
        <div class="hidden md:block md:ml-6 lg:ml-8 xl:ml-12 flex-shrink-0 relative z-20">
          <div class="flex items-center space-x-6">
            <router-link 
              to="/" 
              :class="[
                'text-gray-700 dark:text-gray-200 px-3 py-2 rounded-md text-sm font-semibold transition-all hover:bg-gray-50 dark:hover:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                primaryHoverTextClass,
                primaryHoverTextDarkClass,
                primaryFocusRingClass,
                primaryFocusRingDarkClass,
                { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/' }
              ]"
              aria-label="Zur Startseite"
            >
              Home
            </router-link>
            <router-link 
              to="/about" 
              :class="[
                'text-gray-700 dark:text-gray-200 px-3 py-2 rounded-md text-sm font-semibold transition-all hover:bg-gray-50 dark:hover:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                primaryHoverTextClass,
                primaryHoverTextDarkClass,
                primaryFocusRingClass,
                primaryFocusRingDarkClass,
                { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/about' }
              ]"
              aria-label="Über mich Seite"
            >
              Über mich
            </router-link>
            <router-link 
              to="/portfolio" 
              :class="[
                'text-gray-700 dark:text-gray-200 px-3 py-2 rounded-md text-sm font-semibold transition-all hover:bg-gray-50 dark:hover:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                primaryHoverTextClass,
                primaryHoverTextDarkClass,
                primaryFocusRingClass,
                primaryFocusRingDarkClass,
                { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/portfolio' }
              ]"
              aria-label="Projekte Seite"
            >
              Projekte
            </router-link>
            <router-link 
              to="/contact" 
              :class="[
                'text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2',
                primaryButtonBgClass,
                primaryButtonBgDarkClass,
                primaryButtonHoverClass,
                primaryButtonHoverDarkClass,
                primaryFocusRingClass,
                primaryFocusRingDarkClass,
                { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/contact' }
              ]"
              :style="navigationAccentColor ? { 
                backgroundColor: navigationAccentColor,
                '--tw-ring-color': navigationAccentColor,
                '--hover-bg-color': navigationAccentColor
              } : {}"
              @mouseenter="(e) => {
                if (navigationAccentColor) {
                  const target = e.currentTarget as HTMLElement
                  if (target) {
                    target.style.backgroundColor = navigationAccentColor
                    target.style.opacity = '0.9'
                  }
                }
              }"
              @mouseleave="(e) => {
                if (navigationAccentColor) {
                  const target = e.currentTarget as HTMLElement
                  if (target) {
                    target.style.backgroundColor = navigationAccentColor
                    target.style.opacity = '1'
                  }
                }
              }"
              aria-label="Zum Kontakt"
            >
              <span>Website anfragen</span>
              <span>→</span>
            </router-link>
            <!-- Admin Button (nur wenn angemeldet) -->
            <router-link
              v-if="currentUser"
              to="/admin"
              :class="[
                'p-2 rounded-lg text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800/70 border border-gray-200 dark:border-gray-700/60 dark:hover:border-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                primaryFocusRingClass,
                primaryFocusRingDarkClass,
                { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/admin' }
              ]"
              aria-label="Admin-Bereich"
            >
              <ShieldCheckIcon class="h-5 w-5" aria-hidden="true" />
            </router-link>
            <!-- Theme Toggle Button -->
            <button
              @click="toggleTheme"
              :class="[
                'p-2 rounded-lg text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800/70 border border-gray-200 dark:border-gray-700/60 dark:hover:border-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                primaryFocusRingClass,
                primaryFocusRingDarkClass
              ]"
              :aria-label="resolvedTheme === 'dark' ? 'Zu hellem Theme wechseln' : 'Zu dunklem Theme wechseln'"
            >
              <SunIcon v-if="resolvedTheme === 'dark'" class="h-5 w-5" aria-hidden="true" />
              <MoonIcon v-else class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <!-- Mobile Menu Buttons -->
        <div class="md:hidden flex items-center space-x-2 ml-auto">
          <!-- Mobile Menu Button -->
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            :class="[
              'p-2.5 sm:p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center',
              primaryFocusRingClass,
              primaryFocusRingDarkClass
            ]"
            aria-label="Menü öffnen"
            :aria-expanded="mobileMenuOpen"
          >
            <Bars3Icon v-if="!mobileMenuOpen" class="h-6 w-6" aria-hidden="true" />
            <XMarkIcon v-else class="h-6 w-6" aria-hidden="true" />
          </button>
          <!-- Theme Toggle Button (Mobile) - ganz rechts -->
          <button
            @click="toggleTheme"
            :class="[
              'p-2.5 sm:p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center',
              primaryFocusRingClass,
              primaryFocusRingDarkClass
            ]"
            :aria-label="resolvedTheme === 'dark' ? 'Zu hellem Theme wechseln' : 'Zu dunklem Theme wechseln'"
          >
            <SunIcon v-if="resolvedTheme === 'dark'" class="h-6 w-6" aria-hidden="true" />
            <MoonIcon v-else class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      <!-- Mobile Navigation Menu -->
      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="mobileMenuOpen"
          class="md:hidden mt-4 rounded-2xl shadow-lg shadow-gray-300/40 dark:shadow-black/50 dark:shadow-2xl px-4 py-4 backdrop-blur-md dark:backdrop-blur-xl transition-colors duration-300 bg-white/95 dark:bg-gray-900/95 border border-gray-200/50 dark:border-gray-800/80 dark:border-gray-700/60 relative overflow-hidden pointer-events-auto"
          role="menu"
        >
          <!-- Subtle Dark Mode Glow -->
          <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:opacity-100 opacity-0 pointer-events-none"></div>
          <router-link
            to="/"
            :class="[
              'block px-3 py-2 text-base font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/70 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 relative z-10',
              primaryHoverTextClass,
              primaryHoverTextDarkClass,
              primaryFocusRingClass,
              primaryFocusRingDarkClass,
              { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/' }
            ]"
            role="menuitem"
            @click="mobileMenuOpen = false"
          >
            Home
          </router-link>
          <router-link
            to="/about"
            :class="[
              'block px-3 py-2 text-base font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/70 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 relative z-10',
              primaryHoverTextClass,
              primaryHoverTextDarkClass,
              primaryFocusRingClass,
              primaryFocusRingDarkClass,
              { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/about' }
            ]"
            role="menuitem"
            @click="mobileMenuOpen = false"
          >
            Über mich
          </router-link>
          <router-link
            to="/portfolio"
            :class="[
              'block px-3 py-2 text-base font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/70 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 relative z-10',
              primaryHoverTextClass,
              primaryHoverTextDarkClass,
              primaryFocusRingClass,
              primaryFocusRingDarkClass,
              { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/portfolio' }
            ]"
            role="menuitem"
            @click="mobileMenuOpen = false"
          >
            Projekte
          </router-link>
          <router-link
            to="/contact"
            :class="[
              'block px-3 py-2 text-base font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/70 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 relative z-10',
              primaryHoverTextClass,
              primaryHoverTextDarkClass,
              primaryFocusRingClass,
              primaryFocusRingDarkClass,
              { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/contact' }
            ]"
            role="menuitem"
            @click="mobileMenuOpen = false"
          >
            Website anfragen
          </router-link>
          <router-link
            v-if="currentUser"
            to="/admin"
            :class="[
              'block px-3 py-2 text-base font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/70 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 relative z-10 flex items-center space-x-2',
              primaryHoverTextClass,
              primaryHoverTextDarkClass,
              primaryFocusRingClass,
              primaryFocusRingDarkClass,
              { [`${primaryTextClass} ${primaryTextDarkClass} ${primaryBgClass} ${primaryBgDarkClass}`]: $route.path === '/admin' }
            ]"
            role="menuitem"
            @click="mobileMenuOpen = false"
          >
            <ShieldCheckIcon class="h-5 w-5" aria-hidden="true" />
            <span>Admin</span>
          </router-link>
        </div>
      </transition>

    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { Bars3Icon, XMarkIcon, SunIcon, MoonIcon, ShieldCheckIcon } from '@heroicons/vue/24/outline'
import { useTheme } from '../composables/useTheme'
// Lazy-load Firebase Auth, um initiales Bundle zu reduzieren
import type { User } from 'firebase/auth'

const route = useRoute()

// Prüfe, ob wir auf einer Projektseite sind
const isProjectPage = computed(() => route.path.startsWith('/projects/'))

// Hole Projekt-Accent-Farbe aus CSS-Variable, falls verfügbar
// Jede Projektseite sollte --project-accent setzen
const projectAccentColor = computed(() => {
  if (typeof document === 'undefined' || !isProjectPage.value) return null
  const accentColor = getComputedStyle(document.documentElement).getPropertyValue('--project-accent').trim()
  return accentColor || null
})

// Fallback-Farben für bekannte Projekte (falls CSS-Variable nicht gesetzt)
const getProjectFallbackColor = (path: string): string => {
  if (path.includes('bey-hub')) return '#2c6ae8'
  if (path.includes('m-drei')) return '#a50e34'
  return '#a50e34' // Standard für Projektseiten
}

// Finale Accent-Farbe für Navigation
const navigationAccentColor = computed(() => {
  if (!isProjectPage.value) return null
  return projectAccentColor.value || getProjectFallbackColor(route.path)
})

// Tailwind-Klassen basierend auf Projektseite
// Wenn eine dynamische Accent-Farbe vorhanden ist, verwenden wir inline styles
const primaryTextClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per inline style gesetzt
  return isProjectPage.value ? 'text-[#a50e34]' : 'text-blue-600'
})
const primaryTextDarkClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per inline style gesetzt
  return isProjectPage.value ? 'dark:text-[#a50e34]' : 'dark:text-blue-400'
})
const primaryHoverTextClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per inline style gesetzt
  return isProjectPage.value ? 'hover:text-[#a50e34]' : 'hover:text-blue-600'
})
const primaryHoverTextDarkClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per inline style gesetzt
  return isProjectPage.value ? 'dark:hover:text-[#a50e34]' : 'dark:hover:text-blue-400'
})
const primaryBgClass = computed(() => {
  return isProjectPage.value ? 'bg-red-50' : 'bg-blue-50'
})
const primaryBgDarkClass = computed(() => {
  return isProjectPage.value ? 'dark:bg-red-900/20' : 'dark:bg-blue-900/20'
})
const primaryButtonBgClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per inline style gesetzt
  return isProjectPage.value ? 'bg-[#a50e34]' : 'bg-blue-700'
})
const primaryButtonBgDarkClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per inline style gesetzt
  return isProjectPage.value ? 'dark:bg-[#a50e34]' : 'dark:bg-blue-500'
})
const primaryButtonHoverClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per inline style gesetzt
  return isProjectPage.value ? 'hover:bg-[#8a0c2b]' : 'hover:bg-blue-800'
})
const primaryButtonHoverDarkClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per inline style gesetzt
  return isProjectPage.value ? 'dark:hover:bg-[#8a0c2b]' : 'dark:hover:bg-blue-600'
})
const primaryFocusRingClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per CSS-Variable gesetzt
  return isProjectPage.value ? 'focus:ring-[#a50e34]' : 'focus:ring-blue-700'
})
const primaryFocusRingDarkClass = computed(() => {
  if (navigationAccentColor.value) return '' // Wird per CSS-Variable gesetzt
  return isProjectPage.value ? 'dark:focus:ring-[#a50e34]' : 'dark:focus:ring-blue-400'
})

const mobileMenuOpen = ref<boolean>(false)
const isScrolledDown = ref<boolean>(false)
const lastScrollY = ref<number>(0)
const scrollDownCount = ref<number>(0)
const currentScrollY = ref<number>(0)
const isNameHovered = ref<boolean>(false)
const currentUser = ref<User | null>(null)

// Theme Management
const { resolvedTheme, toggleTheme } = useTheme()

// Progressive Ausblend-Animation basierend auf Scroll-Events
const navOpacity = computed(() => {
  // Nur animieren wenn über 100px gescrollt wurde
  if (currentScrollY.value < 100) return 1
  
  if (scrollDownCount.value === 0) return 1
  if (scrollDownCount.value === 1) return 0.8
  if (scrollDownCount.value === 2) return 0.4
  return 0 // Bei 3 oder mehr
})

const navTransform = computed(() => {
  // Nur animieren wenn über 100px gescrollt wurde
  if (currentScrollY.value < 100) return 'translateY(0)'
  
  if (scrollDownCount.value === 0) return 'translateY(0)'
  if (scrollDownCount.value === 1) return 'translateY(-20%)'
  if (scrollDownCount.value === 2) return 'translateY(-60%)'
  return 'translateY(-100%)' // Bei 3 oder mehr
})

// Scroll-Handler
const handleScroll = () => {
  const scrollY = window.scrollY
  currentScrollY.value = scrollY
  
  // Wenn am Anfang der Seite (unter 100px), Navigation immer anzeigen und Zähler zurücksetzen
  if (scrollY < 100) {
    isScrolledDown.value = false
    scrollDownCount.value = 0
  } else if (scrollY > lastScrollY.value) {
    // Nach unten scrollen - Zähler erhöhen
    scrollDownCount.value++
    // Navigation erst nach 3 Scroll-Events UND über 100px ausblenden
    if (scrollDownCount.value >= 3 && scrollY > 100) {
      isScrolledDown.value = true
    }
  } else {
    // Nach oben scrollen - Navigation einblenden und Zähler zurücksetzen
    isScrolledDown.value = false
    scrollDownCount.value = 0
  }
  
  lastScrollY.value = scrollY
}

onMounted(async () => {
  lastScrollY.value = window.scrollY
  currentScrollY.value = window.scrollY
  window.addEventListener('scroll', handleScroll, { passive: true })
  
  // Lazy-load Firebase Auth
  const { onAuthStateChange, getCurrentUser } = await import('../firebase/auth')
  
  // Initialer Auth-Check
  currentUser.value = getCurrentUser()
  
  // Auth State Observer
  onAuthStateChange((user) => {
    currentUser.value = user
  })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.name-text {
  transition: transform 300ms ease-in-out;
}

.name-hovered {
  transform: scale(1.05);
}

.it-text {
  position: relative;
  display: inline-block;
  margin-left: 3px;
  margin-right: 3px;
}

.it-text::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 2px;
  width: 100%;
  height: 2px;
  background-color: rgb(37, 99, 235); /* blue-600 - default */
  opacity: 1;
  transition: opacity 2000ms ease-in-out, background-color 300ms ease-in-out;
}

.dark .it-text::after {
  background-color: rgb(96, 165, 250); /* blue-400 - default */
}

/* Projektseiten: Dynamische Accent-Farbe für IT-Unterstrich */
[data-project-page="true"] .it-text::after {
  background-color: var(--project-accent, var(--fallback-project-accent, #a50e34)) !important;
}

.dark [data-project-page="true"] .it-text::after {
  background-color: var(--project-accent, var(--fallback-project-accent, #a50e34)) !important;
}

/* Projektseiten: Dynamische Accent-Farbe für Navigation-Links */
[data-project-page="true"] a[class*="hover:text-"]:hover {
  color: var(--project-accent, var(--fallback-project-accent, #a50e34)) !important;
}

.dark [data-project-page="true"] a[class*="dark:hover:text-"]:hover {
  color: var(--project-accent, var(--fallback-project-accent, #a50e34)) !important;
}

/* Projektseiten: Dynamische Accent-Farbe für Kontakt-Button */
[data-project-page="true"] a[href="/contact"],
[data-project-page="true"] a[href="/contact"]:hover,
[data-project-page="true"] a[href="/contact"]:focus,
[data-project-page="true"] router-link[to="/contact"],
[data-project-page="true"] router-link[to="/contact"]:hover,
[data-project-page="true"] router-link[to="/contact"]:focus {
  background-color: var(--project-accent, var(--fallback-project-accent, #a50e34)) !important;
}

[data-project-page="true"] a[href="/contact"]:hover,
[data-project-page="true"] router-link[to="/contact"]:hover {
  opacity: 0.9 !important;
}

.dark [data-project-page="true"] a[href="/contact"],
.dark [data-project-page="true"] a[href="/contact"]:hover,
.dark [data-project-page="true"] a[href="/contact"]:focus,
.dark [data-project-page="true"] router-link[to="/contact"],
.dark [data-project-page="true"] router-link[to="/contact"]:hover,
.dark [data-project-page="true"] router-link[to="/contact"]:focus {
  background-color: var(--project-accent, var(--fallback-project-accent, #a50e34)) !important;
}

.dark [data-project-page="true"] a[href="/contact"]:hover,
.dark [data-project-page="true"] router-link[to="/contact"]:hover {
  opacity: 0.9 !important;
}

/* Projektseiten: Focus Ring */
[data-project-page="true"] a:focus {
  --tw-ring-color: var(--project-accent, var(--fallback-project-accent, #a50e34)) !important;
}

.it-text.it-underline::after {
  opacity: 1;
}
</style>
